import {
  Badge,
  Button,
  Callout,
  EmptyState,
  ErrorState,
  Heading,
  Icon,
  Select,
  Skeleton,
  StatusDot,
  Text,
  UtilityBar,
} from "@imagedesignai/neo";
import { useCallback, useEffect, useRef, useState } from "react";
import type { GifmeApiClient, RunView as Run, RunPagination, ShareLinkView } from "./api-client.ts";
import { RUN_REFRESH_MS, RUN_REQUEST_TIMEOUT_MS } from "./constants.ts";
import { GenerationSettingsView } from "./generation-settings-view.tsx";
import { preferredOutputDownload } from "./library.ts";
import { OwnedImage } from "./media-image.tsx";
import { createRunRefreshCoordinator } from "./run-refresh.ts";
import { ShareButton } from "./share-button.tsx";
import type { StudioPrefill } from "./studio-view.tsx";

const PAGE_SIZE = 12;
type RunOutcomeFilter = "all" | "active" | "ready" | "failed";

export interface RunViewProps {
  api: GifmeApiClient;
  currentUserId: string;
  refreshVersion: number;
  shareLinks: readonly ShareLinkView[];
  onPrepareShare(runId: string): Promise<ShareLinkView>;
  onShare(link: Promise<ShareLinkView>): Promise<void>;
  onRevokeLink(link: ShareLinkView): Promise<void>;
  onPrefill(prefill: StudioPrefill): void;
  onDownload(path: string, filename: string): Promise<void>;
}

function dateLabel(value: string): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(
    new Date(value),
  );
}

function isActive(run: Run): boolean {
  return run.state === "QUEUED" || run.state === "GENERATING" || run.state === "FINALIZING";
}

function statusIntent(run: Run): "positive" | "warning" | "danger" | "neutral" {
  if (run.state === "READY") return "positive";
  if (run.state === "FAILED") return "danger";
  return isActive(run) ? "warning" : "neutral";
}

function LiveLog({ api, runId }: { api: GifmeApiClient; runId: string }) {
  const [log, setLog] = useState("Waiting for logs…");
  const pre = useRef<HTMLPreElement>(null);

  useEffect(() => {
    let isMounted = true;
    const refresh = async () => {
      try {
        const response = await api.request(`/api/runs/${runId}/logs`);
        if (response.status === 404 || !isMounted) return;
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        setLog((await response.text()) || "Waiting for logs…");
      } catch {
        // A transient log miss never replaces the run state.
      }
    };
    void refresh();
    const timer = window.setInterval(() => void refresh(), RUN_REFRESH_MS);
    return () => {
      isMounted = false;
      window.clearInterval(timer);
    };
  }, [api, runId]);

  useEffect(() => {
    if (log.length > 0 && pre.current) pre.current.scrollTop = pre.current.scrollHeight;
  }, [log]);

  return (
    <pre ref={pre} className="run-log">
      {log}
    </pre>
  );
}

function MediaFigure({ api, path, label }: { api: GifmeApiClient; path: string; label: string }) {
  return (
    <figure className="media-figure">
      <OwnedImage api={api} path={path} alt={label} />
      <figcaption className="mono">{label}</figcaption>
    </figure>
  );
}

function linkFor(links: readonly ShareLinkView[], runId: string): ShareLinkView | null {
  return links.find((link) => link.runId === runId) ?? null;
}

export function RunCard({
  api,
  run,
  open,
  currentUserId,
  shareLinks,
  onToggle,
  onPrepareShare,
  onShare,
  onRevokeLink,
  onPrefill,
  onDownload,
}: {
  api: GifmeApiClient;
  run: Run;
  open: boolean;
  currentUserId: string;
  shareLinks: readonly ShareLinkView[];
  onToggle(): void;
  onPrepareShare(runId: string): Promise<ShareLinkView>;
  onShare(link: Promise<ShareLinkView>): Promise<void>;
  onRevokeLink(link: ShareLinkView): Promise<void>;
  onPrefill(prefill: StudioPrefill): void;
  onDownload(path: string, filename: string): Promise<void>;
}) {
  const bodyId = `run-${run.id}-body`;
  const isOwner = run.ownerUserId === currentUserId;
  const link = linkFor(shareLinks, run.id);
  const canPrefill = Boolean(run.selfieId && run.sourceGifId && run.sourcePreview);
  const prefill = () => {
    if (!run.selfieId || !run.sourceGifId || !run.sourcePreview) return;
    onPrefill({
      key: crypto.randomUUID(),
      selfieId: run.selfieId,
      sources: [
        {
          originalName: run.sourceOriginalName,
          source: {
            sourceGifId: run.sourceGifId,
            sourceOriginalName: run.sourceOriginalName,
            createdAt: run.createdAt,
            mediaUrl: run.sourcePreview,
          },
        },
      ],
    });
  };

  return (
    <article className="run-card">
      <button
        type="button"
        className="run-summary"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={onToggle}
      >
        <StatusDot intent={statusIntent(run)}>{run.state.toLowerCase()}</StatusDot>
        <span className="run-meta mono">
          {run.id.slice(0, 8)} · {run.ownerEmail} · {dateLabel(run.createdAt)}
        </span>
        <Badge size="sm" tone="subtle" intent="neutral">
          single
        </Badge>
        <Icon name={open ? "chevron-up" : "chevron-down"} size="inline" />
      </button>
      {open ? (
        <div id={bodyId} className="run-body">
          <GenerationSettingsView
            contractVersion={run.generationSettingsContractVersion}
            settings={run.generationSettings}
          />
          {isActive(run) ? <LiveLog api={api} runId={run.id} /> : null}
          {run.media ? (
            <>
              <div className="triptych">
                <MediaFigure api={api} path={run.media.selfie} label="selfie" />
                <MediaFigure api={api} path={run.media.source} label="input gif" />
                <MediaFigure api={api} path={run.media.output} label="output gif" />
              </div>
              <UtilityBar
                className="run-actions"
                label="Run actions"
                primaryLabel="Download"
                primaryIcon="download"
                onPrimary={() => {
                  const download = preferredOutputDownload(
                    run.id,
                    run.media?.output as string,
                    run.media?.outputMp4,
                  );
                  return onDownload(download.path, download.filename);
                }}
                actions={
                  isOwner
                    ? [
                        ...(canPrefill
                          ? [{ label: "Recast", icon: "refresh-cw" as const, onClick: prefill }]
                          : []),
                        ...(link
                          ? [
                              {
                                label: "Revoke link",
                                icon: "x" as const,
                                onClick: () => void onRevokeLink(link),
                              },
                            ]
                          : []),
                      ]
                    : []
                }
              />
              {isOwner ? (
                <ShareButton
                  link={link}
                  onPrepare={() => onPrepareShare(run.id)}
                  onShare={onShare}
                />
              ) : null}
            </>
          ) : null}
          {run.state === "FAILED" ? (
            <>
              {run.sourcePreview ? (
                <MediaFigure api={api} path={run.sourcePreview} label="input gif" />
              ) : null}
              <Callout
                intent="danger"
                label="Run failure"
                approveLabel={isOwner && canPrefill ? "Retry run" : undefined}
                onApprove={isOwner && canPrefill ? prefill : undefined}
              >
                {[run.failureCode, run.error].filter(Boolean).join(" · ") || "Generation failed"}
              </Callout>
            </>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

export function RunView({
  api,
  currentUserId,
  refreshVersion,
  shareLinks,
  onPrepareShare,
  onShare,
  onRevokeLink,
  onPrefill,
  onDownload,
}: RunViewProps) {
  const [runs, setRuns] = useState<Run[]>([]);
  const [pagination, setPagination] = useState<RunPagination | null>(null);
  const [outcome, setOutcome] = useState<RunOutcomeFilter>("all");
  const [page, setPage] = useState(1);
  const [openIds, setOpenIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const refreshCoordinator = useRef(createRunRefreshCoordinator());

  const refresh = useCallback(
    async (replace = false) => {
      const attempt = refreshCoordinator.current.start({
        replace,
        timeoutMs: RUN_REQUEST_TIMEOUT_MS,
      });
      if (!attempt) return;
      try {
        const query = new URLSearchParams({
          outcome,
          limit: String(PAGE_SIZE),
          offset: String((page - 1) * PAGE_SIZE),
        });
        const payload = await api.json<{ runs: Run[]; pagination: RunPagination }>(
          `/api/runs?${query}`,
          { signal: attempt.signal },
        );
        if (!attempt.isCurrent()) return;
        setRuns(payload.runs);
        setPagination(payload.pagination);
        setPage(payload.pagination.page);
        setOpenIds(
          (current) =>
            new Set([...current].filter((id) => payload.runs.some((run) => run.id === id))),
        );
        setError(null);
      } catch (reason) {
        if (!attempt.isCurrent()) return;
        setError(reason instanceof Error ? reason.message : String(reason));
      } finally {
        const isCurrent = attempt.isCurrent();
        attempt.finish();
        if (isCurrent) setIsLoading(false);
      }
    },
    [api, outcome, page],
  );

  useEffect(() => {
    void refreshVersion;
    void refresh();
    const timer = window.setInterval(() => void refresh(), RUN_REFRESH_MS);
    return () => {
      window.clearInterval(timer);
      refreshCoordinator.current.cancel();
    };
  }, [refresh, refreshVersion]);

  const setFilter = (value: string) => {
    setPage(1);
    setOutcome(value as RunOutcomeFilter);
  };

  return (
    <section className="runs-section" aria-labelledby="runs-title">
      <div className="runs-heading">
        <Heading id="runs-title" level={2} scale="md">
          Runs
        </Heading>
        <Text kind="caption" tone="muted" aria-live="polite">
          {pagination
            ? `${pagination.total} run${pagination.total === 1 ? "" : "s"} · page ${pagination.page} of ${pagination.pageCount}`
            : "Loading runs…"}
        </Text>
      </div>
      <div className="filter-well">
        <Select
          label="Outcome"
          value={outcome}
          options={[
            { label: "All outcomes", value: "all" },
            { label: "Active", value: "active" },
            { label: "Ready", value: "ready" },
            { label: "Failed", value: "failed" },
          ]}
          onChange={setFilter}
        />
      </div>
      {isLoading ? <Skeleton arrangement="rows" label="Loading runs" /> : null}
      {error ? (
        <ErrorState
          title="Could not load runs"
          description={error}
          action={
            <Button
              intent="secondary"
              size="sm"
              icon="refresh-cw"
              onClick={() => void refresh(true)}
            >
              Retry runs
            </Button>
          }
          aria-live="polite"
        />
      ) : null}
      {!isLoading && !error && runs.length === 0 ? (
        <EmptyState title="No runs yet" description="Cast your first GIF to see it here." />
      ) : null}
      <div className="run-list">
        {runs.map((run) => (
          <RunCard
            key={run.id}
            api={api}
            run={run}
            open={openIds.has(run.id)}
            currentUserId={currentUserId}
            shareLinks={shareLinks}
            onToggle={() =>
              setOpenIds((current) => {
                const next = new Set(current);
                if (next.has(run.id)) next.delete(run.id);
                else next.add(run.id);
                return next;
              })
            }
            onPrepareShare={onPrepareShare}
            onShare={onShare}
            onRevokeLink={onRevokeLink}
            onPrefill={onPrefill}
            onDownload={onDownload}
          />
        ))}
      </div>
      {pagination ? (
        <nav className="run-pagination" aria-label="Runs pages">
          <Button
            intent="secondary"
            size="sm"
            icon="chevron-left"
            disabled={pagination.page <= 1}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
          >
            Previous
          </Button>
          <Text kind="caption" tone="muted" aria-live="polite">
            page {pagination.page} of {pagination.pageCount}
          </Text>
          <Button
            intent="secondary"
            size="sm"
            trailingIcon="chevron-right"
            disabled={pagination.page >= pagination.pageCount}
            onClick={() => setPage((current) => current + 1)}
          >
            Next
          </Button>
        </nav>
      ) : null}
    </section>
  );
}
